import React, { useRef, useEffect, useState } from 'react';
import { useGetChatMessagesQuery, useMarkAsReadMutation, useSendMessageMutation } from '../../redux/apis/endpoints/messageApi';
import { useConfirmPickupMutation } from '../../redux/apis/endpoints/deliveryApi';
import { useGetFulfillmentGroupDetailsQuery, useGetOrderDetailsQuery } from '../../redux/apis/endpoints/orderApi';
import MessageItem from './MessageItem';
import MessageInput from './MessageInput';
import TypingIndicator from './components/TypingIndicator';
import ProductPreview from './components/ProductPreview';
import FulfillmentGroupItemsPreview from './fulfillment group/FulfillmentGroupItemsPreview';
import FulfillmentGroupControls from './fulfillment group/FulfillmentGroupControls';
import { ChevronLeftIcon } from '@heroicons/react/24/solid';
import LoadingSpinner from '../common/LoadingSpinner';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import UserProfilePic from '../common/UserProfilePic';
import MessageContextButton from './components/MessageContextButton';
import OrderContextSelector from './components/OrderContextSelector';

// Helper to get a clean ID (non-empty string or null)
const getCleanId = (idVal) => {
  const idStr = idVal ? String(idVal).trim() : null;
  return idStr && idStr !== "" ? idStr : null;
};

const MessageContainer = ({ chat, fulfillmentGroupId, orderId, productContext, currentUser, onBack, className = '', diagnosticId }) => {
  console.log('[MessageContainer] Rendering with props:', { 
    hasFgId: !!fulfillmentGroupId,
    hasOrderId: !!orderId,
    hasProductContext: !!productContext,
    fulfillmentGroupId,
    orderId,
    chatId: chat?.id,
    diagnosticId 
  });

  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const [hasScrolled, setHasScrolled] = useState(false);
  const { typingUsers } = useSelector(state => state.chat || { typingUsers: {} });
  const [markAsRead] = useMarkAsReadMutation();
  const [sendMessage] = useSendMessageMutation();
  
  const [confirmPickup, { isLoading: isConfirmingPickup }] = useConfirmPickupMutation();

  const effectiveChatId = fulfillmentGroupId || orderId || productContext?.merchant?.id || productContext?.users_permissions_user?.id || chat?.id;

  const { 
    data: fulfillmentGroupDetails, 
    isLoading: isLoadingFgDetails, 
    error: fgDetailsError,
    refetch: refetchFulfillmentGroupDetails
  } = useGetFulfillmentGroupDetailsQuery(fulfillmentGroupId, {
    skip: !fulfillmentGroupId,
  });

  const { 
    data: orderDetails,
    isLoading: isLoadingOrderDetails,
    error: orderDetailsError
  } = useGetOrderDetailsQuery(orderId, {
    skip: !orderId,
  });

  // Determine user role first
  let userRole = null;
  if (fulfillmentGroupId && fulfillmentGroupDetails && currentUser) {
    if (fulfillmentGroupDetails.merchant?.id == currentUser.id) {
      userRole = 'seller';
    } else if (fulfillmentGroupDetails.customer?.id == currentUser.id) {
      userRole = 'buyer';
    }
  } else if (orderId && orderDetails && currentUser) {
    if (orderDetails.merchants?.some(merchant => merchant.id == currentUser.id)) {
      userRole = 'seller';
    } else if (orderDetails.users_permissions_user?.id == currentUser.id) {
      userRole = 'buyer';
    }
  }

  // Now determine recipient ID using userRole
  let recipientId = null;

  if (fulfillmentGroupId && fulfillmentGroupDetails) {
    if (userRole === 'seller') {
      recipientId = getCleanId(fulfillmentGroupDetails.customer?.id);
    } else {
      recipientId = getCleanId(fulfillmentGroupDetails.merchant?.id);
    }
  } else if (orderId && orderDetails) {
    if (userRole === 'seller') {
      recipientId = getCleanId(orderDetails.users_permissions_user?.id);
    } else if (userRole === 'buyer' && orderDetails.merchants && orderDetails.merchants.length > 0) {
      recipientId = getCleanId(orderDetails.merchants[0].id);
    }
  } else if (productContext) {
    const seller = productContext.merchant || productContext.users_permissions_user;
    const sellerIdFromProduct = getCleanId(seller?.id);
    const currentUserIdClean = getCleanId(currentUser?.id);

    if (sellerIdFromProduct && sellerIdFromProduct !== currentUserIdClean) {
      recipientId = sellerIdFromProduct;
    } else if (sellerIdFromProduct && sellerIdFromProduct === currentUserIdClean) {
      // User is the seller. Chat.id should be the same or also point to the seller.
      const chatIdFromChatObj = getCleanId(chat?.id);
      if (chatIdFromChatObj) { 
        recipientId = chatIdFromChatObj;
      } else {
        console.warn('[MessageContainer] User is seller, but clean chat ID is also null.');
        // recipientId remains null
      }
    } else { // sellerIdFromProduct is null (original was null, undefined, or "")
      // Fallback to chat.id if seller info in productContext was invalid
      const chatIdFromChatObj = getCleanId(chat?.id);
      if (chatIdFromChatObj) {
        recipientId = chatIdFromChatObj;
        console.warn('[MessageContainer] Used clean chat.id as recipientId due to invalid/missing sellerId in productContext.');
      } else {
        console.warn('[MessageContainer] Invalid/missing sellerId in productContext, and clean chat.id is also null.');
        // recipientId remains null
      }
    }
  } else if (chat) { // Fallback for general chats not tied to product/order context via URL
    recipientId = getCleanId(chat?.id);
  }

  // console.log('[MessageContainer] Determined recipientId:', recipientId, 'for currentUser:', currentUser?.id);

  // Use the unified useGetChatMessagesQuery for all cases
  const { 
    data: messagesData,
    isLoading: isLoadingMessages, 
    error: messagesError,
  } = useGetChatMessagesQuery(
    (currentUser && recipientId) ? 
      { senderId: String(currentUser.id), receiverId: String(recipientId) } : 
      undefined, 
    { 
      skip: !currentUser?.id || !recipientId,
    }
  );
  
  const messages = messagesData;

  const isLoading = isLoadingMessages || 
                   (fulfillmentGroupId && isLoadingFgDetails) ||
                   (orderId && isLoadingOrderDetails);
                   
  const error = messagesError || 
               (fulfillmentGroupId && fgDetailsError) ||
               (orderId && orderDetailsError);

  // For pickup fulfillment group
  const isPickupCompleted = fulfillmentGroupDetails?.status === 'pickup_completed_by_customer';
  const shouldRenderFulfillmentGroupControlsArea = fulfillmentGroupId && 
                                               fulfillmentGroupDetails?.groupType === 'pickup';

  useEffect(() => {
    if (effectiveChatId && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      setHasScrolled(false);
    }
  }, [effectiveChatId]);
  
  useEffect(() => {
    if (messages && messages.length > 0 && currentUser && effectiveChatId) {
      const unreadMessageIds = messages
        .filter(message => {
          const isReceiver = !(message.sender?.id === currentUser.id || message.isSender);
          return isReceiver && (!message.status || message.status.read !== true);
        })
        .map(message => message.id);
      
      if (unreadMessageIds.length > 0) {
        markAsRead({ messageIds: unreadMessageIds })
          .catch(readError => console.error('Failed to mark messages as read:', readError));
      }
    }
  }, [messages, currentUser, markAsRead, effectiveChatId]);
  
  useEffect(() => {
    if (messagesEndRef.current && !hasScrolled && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, hasScrolled]);
  
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
      setHasScrolled(!isAtBottom);
    };
    
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => {
    console.log('[MessageContainer] fulfillmentGroupDetails updated:', fulfillmentGroupDetails);
    if (fulfillmentGroupDetails) {
      console.log('[MessageContainer] Current FG status:', fulfillmentGroupDetails.status);
    }
  }, [fulfillmentGroupDetails]);
  
  const handleConfirmPickup = async (enteredCode) => {
    if (!fulfillmentGroupDetails || !enteredCode) {
      toast.error('Pickup code is required.');
      return;
    }
    try {
      await confirmPickup({ fulfillmentGroupId: fulfillmentGroupDetails.id, code: enteredCode }).unwrap();
      toast.success('Pickup confirmed successfully!');
      refetchFulfillmentGroupDetails();
    } catch (err) {
      toast.error(err.data?.message || err.message || 'Failed to confirm pickup. Please check the code and try again.');
    }
  };
  
  const handleSendProductContext = async () => {
    if (!currentUser || !recipientId || !productContext) {
      toast.error('Cannot send product information. Missing details.');
      return;
    }

    try {
      const text = `Regarding product: ${productContext.name}`;
      await sendMessage({
        receiverId: String(recipientId),
        text: text,
        productId: productContext.id,
        messageType: "product_context",
        senderId: String(currentUser.id),
      }).unwrap();

      toast.success('Product information sent!');
    } catch (error) {
      console.error('Failed to send product context:', error);
      toast.error('Failed to send product context. Please try again.');
    }
  };
  
  const handleSendOrderContext = async (fgId) => {
    if (!currentUser || !recipientId) {
      return;
    }
    
    try {
      const contextMessage = `Order Context (FG-${fgId})`;
      
      const result = await sendMessage({
        receiverId: recipientId,
        text: contextMessage,
        fulfillmentGroupId: fgId,
        senderId: currentUser.id,
        messageType: "fulfillment_context"
      }).unwrap();
      
      // Wait a short time to ensure the message has been processed
      await new Promise(resolve => setTimeout(resolve, 300));
      
      toast.success('Order context shared');
      return result;
    } catch (error) {
      console.error('Failed to send order context:', error);
      toast.error('Failed to send order context');
      throw error;
    }
  };
  
  const isTyping = typingUsers[effectiveChatId] || false;
  
  const currentChatContext = fulfillmentGroupId ? fulfillmentGroupDetails : 
                            orderId ? orderDetails : 
                            productContext ? productContext :
                            chat;
  
  // Define header details based on context
  let headerTitle = 'Chat';
  let headerSubtitle = null;
  let avatarUrl = null;
  let chatPartnerUser = null; // Initialize chat partner user object

  if (fulfillmentGroupId && fulfillmentGroupDetails) {
    if (userRole === 'buyer') {
      headerTitle = fulfillmentGroupDetails.merchant?.username || 'Merchant';
      avatarUrl = fulfillmentGroupDetails.merchant?.avatar?.url;
      chatPartnerUser = fulfillmentGroupDetails.merchant;
    } else { // User is seller or other role
      headerTitle = fulfillmentGroupDetails.customer?.username || 'Customer';
      avatarUrl = fulfillmentGroupDetails.customer?.avatar?.url;
      chatPartnerUser = fulfillmentGroupDetails.customer;
    }
  } else if (orderId && orderDetails) {
    if (userRole === 'buyer' && orderDetails.merchants && orderDetails.merchants.length > 0) {
      headerTitle = orderDetails.merchants[0].username || 'Merchant';
      avatarUrl = orderDetails.merchants[0].avatar?.url;
      chatPartnerUser = orderDetails.merchants[0];
    } else { // User is seller or other role, or buyer but no merchant info (should not happen for valid orders)
      headerTitle = orderDetails.users_permissions_user?.username || 'Buyer';
      avatarUrl = orderDetails.users_permissions_user?.avatar?.url;
      chatPartnerUser = orderDetails.users_permissions_user;
    }
  } else if (productContext) {
    const seller = productContext.merchant || productContext.users_permissions_user;
    headerTitle = seller?.username || 'Seller';
    avatarUrl = seller?.avatar?.url;
    chatPartnerUser = seller;
  } else if (chat) { // This is for general chats where chat object itself is the partner
    headerTitle = chat.username;
    avatarUrl = chat.avatar;
    chatPartnerUser = chat; // The chat object is the user profile
    if (chat.product) {
      headerSubtitle = `Product: ${chat.product.name}`;
    }
  }
  
  if (!fulfillmentGroupId && !orderId && !productContext && !chat) {
    return (
      <div className={`flex flex-col items-center justify-center bg-brand-teal bg-opacity-5 ${className}`}>
        <div className="text-center p-6">
          <div className="mx-auto h-12 w-12 text-brand-teal">
            <svg className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Select a conversation</h3>
          <p className="mt-2 text-sm text-gray-500">
            Choose a chat from the list to start messaging
          </p>
        </div>
      </div>
    );
  }
  
  if (isLoading) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <LoadingSpinner />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center p-6 ${className}`}>
        <div className="mx-auto h-12 w-12 text-red-500">
          <svg className="h-full w-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="mt-4 text-lg font-medium text-gray-900">Failed to load chat</h3>
        <p className="mt-2 text-sm text-gray-500">
          {error.data?.message || error.message || 'An error occurred'}
        </p>
      </div>
    );
  }
  
  return (
    <div className={`flex flex-col h-full ${className}`}>
      <div className="border-b border-gray-200 flex-shrink-0 px-4 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center flex-grow min-w-0">
          {onBack && (
            <button 
              onClick={onBack}
                className="p-1 mr-2 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 md:hidden"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
          )}
            <UserProfilePic 
              user={chatPartnerUser} // Use the determined chatPartnerUser
              className="w-8 h-8 flex-shrink-0" 
              altText={headerTitle}
            />
            <div className="ml-2.5 min-w-0">
              <h2 className="text-base font-medium text-gray-900 truncate">{headerTitle}</h2>
              {headerSubtitle && (
                <p className="text-sm text-gray-500">{headerSubtitle}</p>
              )}
            </div>
          </div>
          
          {/* Show fulfillment group items if in FG context */}
          {fulfillmentGroupId && fulfillmentGroupDetails?.orderItems && (
            <div className="ml-auto flex-shrink-0 max-w-[75%]">
              <FulfillmentGroupItemsPreview 
                orderItems={fulfillmentGroupDetails.orderItems} 
                showDeliveryMethod={false}
                fulfillmentGroup={fulfillmentGroupDetails}
              />
          </div>
          )}
        </div>
      </div>
      
      {/* Scrollable Messages Area */}
      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 pb-6"
      >
        {/* DEBUG LOG FOR RENDER SECTION */}
        { (fulfillmentGroupId || orderId) && console.log('[MessageContainer DEBUG RENDER] About to render messages or no messages text. Messages count:', messages?.length) }
        {messages && messages.length > 0 ? (
          messages.map((msg, index) => (
            <MessageItem 
              key={msg.id || index} 
              message={msg} 
              currentUser={currentUser} 
              productContextForDisplay={msg.messageType === 'product_context' ? msg.product : null}
            />
          ))
        ) : (
          <div className="text-center p-4">
            <p className="text-sm text-gray-500">No messages yet</p>
            {(fulfillmentGroupId || orderId) && isLoadingMessages && (
              <p className="text-xs text-blue-500 mt-1">Loading messages...</p>
            )}
            {(fulfillmentGroupId || orderId) && !isLoadingMessages && (
              <p className="text-xs text-gray-400 mt-1">Start a conversation about this order</p>
            )}
          </div>
        )}
        
        {isTyping && (
          <TypingIndicator username={headerTitle} />
        )}
        
        <div ref={messagesEndRef} className="h-2" />
      </div>
      
      {/* Controls like FulfillmentGroupControls, OrderContextSelector etc. */}
      {shouldRenderFulfillmentGroupControlsArea && fulfillmentGroupDetails && (
        <FulfillmentGroupControls 
          userRole={userRole}
          fulfillmentGroupDetails={fulfillmentGroupDetails}
          isPickupCompleted={isPickupCompleted}
          handleConfirmPickup={handleConfirmPickup}
          isConfirmingPickup={isConfirmingPickup}
        />
      )}
      
      {orderId && orderDetails && (
        <OrderContextSelector 
          orderId={orderId}
          recipientId={recipientId}
          onSendContext={handleSendOrderContext}
        />
      )}
      
      {/* Order Context Bar - Placed before product context and message input */}
      {fulfillmentGroupId && fulfillmentGroupDetails && (
        <div className="px-4 py-2 border-t border-gray-200 flex-shrink-0 bg-gray-50">
          <div className="flex items-center justify-between text-sm">
            <div className="font-medium text-gray-700">
              {fulfillmentGroupDetails.groupType === 'shipping' ? 
                (
                  <div className="flex items-center">
                    <span className="mr-1">📦</span>
                    <span>Shipping Order: {fulfillmentGroupDetails.status?.replace(/_/g, ' ')}</span>
                  </div>
                ) : 
                (
                  <div className="flex items-center">
                    <span className="mr-1">🏬</span>
                    <span>Pickup Order: {fulfillmentGroupDetails.status?.replace(/_/g, ' ')}</span>
                  </div>
                )
              }
            </div>
            <MessageContextButton 
              fulfillmentGroupId={fulfillmentGroupId} 
              recipientId={recipientId} 
            />
          </div>
        </div>
      )}

      {/* Product Context Area - Always show if productContext exists (and not FG/Order chat) */}
      {productContext && !fulfillmentGroupId && !orderId && (
        <div className="p-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex-grow min-w-0 mr-2">
            <ProductPreview product={productContext} />
          </div>
          <button 
            onClick={handleSendProductContext}
            className="bg-brand-teal text-white px-4 py-2 rounded-md text-sm hover:bg-brand-teal-dark focus:outline-none focus:ring-2 focus:ring-brand-teal-dark focus:ring-opacity-50 flex-shrink-0"
          >
            Send Product Info
          </button>
        </div>
      )}
      
      {/* Message Input Area */}
      <div className="flex-shrink-0">
        <MessageInput
          recipientId={recipientId}
          productId={chat?.product?.id} // Do not pass productContext.id here automatically
          fulfillmentGroupId={fulfillmentGroupId}
        />
      </div>
    </div>
  );
};

export default MessageContainer; 